/**
 * @file ProfitAnalysisReportSheet.tsx
 * @description 수익분석 리포트 시트 래퍼 (Client Component)
 *
 * 주요 기능:
 * 1. Sheet로 ProfitAnalysisReport를 감싸는 래퍼 컴포넌트
 * 2. 로딩 상태 관리 및 스피너 표시
 * 3. 시트 열기/닫기 제어
 *
 * @dependencies
 * - react: useState
 * - @/components/shared/ui/sheet: Sheet, SheetContent, SheetTitle, SheetTrigger
 * - @/components/shared/common: Spinner
 * - @/components/simulations/reports/ProfitAnalysisReportOne: ProfitAnalysisReportOne
 * - @/lib/types: Profit, Valuation, Costs, AuctionSummary
 */

"use client";

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/shared/ui/sheet";
import { Spinner } from "@/components/shared/common";
import { ProfitAnalysisReportOne } from "@/components/simulations/reports/ProfitAnalysisReportOne";
import type { Profit, Valuation, Costs, AuctionSummary, Property } from "@/lib/core/types";

interface ProfitAnalysisReportSheetProps {
  profit?: Profit;
  valuation?: Valuation;
  costs?: Costs;
  summary?: AuctionSummary;
  property?: Property;
  userBid?: number;
  simulationId?: string;
  caseId?: string;
  outcome?: "success" | "failed"; // 낙찰 결과 (선택적)
  trigger: React.ReactNode;
  /** 대시보드 히스토리 등, 이미 결제된 리포트를 여는 경우 true */
  isHistory?: boolean;
}

export function ProfitAnalysisReportSheet({
  profit: initialProfit,
  valuation: initialValuation,
  costs: initialCosts,
  summary: initialSummary,
  property: initialProperty,
  userBid,
  simulationId,
  caseId,
  outcome,
  trigger,
  isHistory = false,
}: ProfitAnalysisReportSheetProps) {
  const [open, setOpen] = useState(false);
  const [savedReport, setSavedReport] = useState<Record<string, unknown> | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const profit = (savedReport?.profit as Profit | undefined) || initialProfit;
  const valuation =
    (savedReport?.valuation as Valuation | undefined) || initialValuation;
  const costs = (savedReport?.costs as Costs | undefined) || initialCosts;
  const summary =
    (savedReport?.summary as AuctionSummary | undefined) || initialSummary;
  const property =
    (savedReport?.property as Property | undefined) || initialProperty;

  useEffect(() => {
    if (!open || !isHistory) {
      return;
    }

    if (initialProfit && initialValuation && initialCosts && initialSummary) {
      return;
    }

    let ignore = false;

    async function loadSavedReport() {
      setIsLoadingReport(true);

      try {
        const response = await fetch("/api/auction/saved-reports?limit=50&offset=0");
        const data = await response.json();
        const profitReport = data.items?.find(
          (item: { caseNo?: string; type?: string; result_snapshot?: Record<string, unknown> }) =>
            (item.caseNo === simulationId || item.caseNo === caseId) &&
            item.type === "profit",
        );

        if (!ignore) {
          setSavedReport(profitReport?.result_snapshot || null);
        }
      } catch {
        if (!ignore) {
          setSavedReport(null);
        }
      } finally {
        if (!ignore) {
          setIsLoadingReport(false);
        }
      }
    }

    loadSavedReport();

    return () => {
      ignore = true;
    };
  }, [
    open,
    isHistory,
    initialProfit,
    initialValuation,
    initialCosts,
    initialSummary,
    simulationId,
    caseId,
  ]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {trigger}
        </SheetTrigger>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl p-0 flex flex-col overflow-hidden"
        >
          <SheetTitle className="sr-only">수익분석 리포트</SheetTitle>
          {isLoadingReport ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 sm:py-16">
              <Spinner size="lg" className="text-emerald-600" />
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                저장된 리포트를 불러오는 중...
              </p>
            </div>
          ) : profit && valuation && costs && summary ? (
            <div className="flex-1 overflow-y-auto min-h-0 px-3 sm:px-4 md:px-6 pb-4 sm:pb-6" data-lenis-prevent>
              <ProfitAnalysisReportOne
                profit={profit}
                valuation={valuation}
                costs={costs}
                summary={summary}
                property={property}
                userBid={userBid}
                simulationId={simulationId}
                outcome={outcome}
              />
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 sm:py-16">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                리포트 데이터를 불러올 수 없습니다.
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
  );
}
